import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { readableOn } from '../../lib/readableInk.js';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  Input,
  Label,
  Loading,
  Textarea,
} from '@attendance/ui';
import type { CompanySettings } from '@attendance/shared/schemas/settings';
import { brandingApi, logoUrl } from '../../api/branding.api.js';
import { settingsApi } from '../../api/settings.api.js';
import { useBrandingStore } from '../../stores/brandingStore.js';
import { friendlyMessage } from '../../lib/errors.js';
import { stickyBelowToolbar } from '../../components/common/PageShell.js';
import { ColorField, HEX_COLOR_RE } from './BrandingColorField.js';

type BrandingDraft = CompanySettings['branding'];

const DEFAULT: BrandingDraft = {
  primaryColor: '#6C47FF',
  appName: 'Attendance',
  logoUrl: null,
  emailFooter: null,
};

/**
 * Branding — one card of the "Company" settings tab, not its own page.
 * `SettingsPage.tsx`'s `CompanyIdentityBlock` mounts this beside
 * `CompanyInfoSection`; `/settings/branding` used to be this content's own
 * route and is now a redirect to `/settings/g/company` (see App.tsx).
 */
export function BrandingSection(): JSX.Element {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const refreshBranding = useBrandingStore((s) => s.refresh);
  const storeVersion = useBrandingStore((s) => s.version);

  const {
    data: serverBranding,
    isError: loadFailed,
    error: loadError,
    refetch: reloadBranding,
  } = useQuery({
    queryKey: ['settings', 'branding'],
    queryFn: () => settingsApi.getCategory('branding'),
  });
  // `DEFAULT` is the stock violet brand, not an empty draft — and the save PUTs
  // the draft wholesale. Shown while the tenant's real branding is unread, this
  // page offers to discard their colour, app name and email footer, and the
  // preview panel under "What employees will see" shows the wrong brand as if
  // it were theirs. Nothing renders until the stored branding is in hand.
  const notLoaded = serverBranding === undefined;
  const { data: companyInfo } = useQuery({
    queryKey: ['settings', 'info'],
    queryFn: settingsApi.getInfo,
  });

  const [draft, setDraft] = useState<BrandingDraft>(DEFAULT);
  const [hasLogo, setHasLogo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  // Hydrate draft from server when fetched
  useEffect(() => {
    if (serverBranding) {
      setDraft({ ...DEFAULT, ...serverBranding });
    }
  }, [serverBranding]);

  useEffect(() => {
    if (companyInfo) {
      setHasLogo(!!companyInfo.logoUrl);
    }
  }, [companyInfo]);

  /**
   * THE LIVE PREVIEW WROTE TO THE WHOLE APP AND NEVER SWEPT UP.
   *
   * `handleColorChange` sets `--brand-primary` INLINE on `<html>` so the preview
   * panel matches while a colour is being chosen. That variable is not local to
   * this page: `applyBranding` in `stores/brandingStore.ts` sets the same inline
   * property, and `index.css` reads it for the `.brand-*` helpers and the login
   * gradient. So an admin who tried a colour and then navigated away WITHOUT
   * saving left the app wearing a brand nobody stored — until the next full
   * reload, since `refresh()` is the only other writer and nothing calls it on
   * an abandoned edit.
   *
   * The unmount restores the last SAVED colour from the store. After a
   * successful save that store has already been refreshed to the new value, so
   * this is a no-op on the happy path and a repair on every other one.
   */
  useEffect(
    () => () => {
      const stored = useBrandingStore.getState().branding?.primaryColor;
      if (stored) {
        document.documentElement.style.setProperty('--brand-primary', stored);
      } else {
        // Never bootstrapped: drop the inline override so the stylesheet's own
        // light/dark split takes over again rather than freezing a draft hex.
        document.documentElement.style.removeProperty('--brand-primary');
      }
    },
    []
  );

  const saveBranding = useMutation({
    mutationFn: () => settingsApi.updateCategory('branding', draft),
    onSuccess: async () => {
      /* §3.12: this used to set an inline "Saved." that cleared itself after
         2.5s — a toast wearing a paragraph's clothes, and the second copy of a
         fact once a real toast exists. The toast is the success channel here;
         FAILURE stays inline (`error`), beside the fields it refers to. */
      toast.success(t('settings.branding.toast.saved', 'Branding updated'));
      setError(null);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['settings', 'branding'] }),
        qc.invalidateQueries({ queryKey: ['settings', 'info'] }),
      ]);
      await refreshBranding();
    },
    onError: (err) => setError(friendlyMessage(err)),
  });

  const uploadLogo = useMutation({
    mutationFn: (file: File) => brandingApi.uploadLogo(file),
    onSuccess: async () => {
      toast.success(t('settings.branding.toast.logoUploaded', 'Logo uploaded'));
      setError(null);
      setHasLogo(true);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['settings', 'info'] }),
      ]);
      await refreshBranding();
    },
    onError: (err) => setError(friendlyMessage(err)),
  });

  const primaryColorValid = HEX_COLOR_RE.test(draft.primaryColor);
  // secondaryColor is not part of the public branding payload and nothing reads
  // it, so its control was removed; only the primary colour is validated/gated.
  // Its leftover default sat in `DEFAULT` above until it broke the build: the
  // shared `CompanySettings['branding']` has no such field, so the object
  // literal stopped typechecking, and the raw hex was a token bypass with no
  // allowlist entry besides.
  const colorsValid = primaryColorValid;

  function handleColorChange(key: 'primaryColor', value: string): void {
    setDraft((d) => ({ ...d, [key]: value }));
    if (HEX_COLOR_RE.test(value)) {
      // Live-update the document so the preview matches before saving — only
      // for a valid 6-digit hex, so a half-typed value doesn't blank the brand.
      document.documentElement.style.setProperty('--brand-primary', value);
    }
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError(t('settings.branding.logo.tooLarge', 'Logo must be under 2 MB'));
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError(
        t(
          'settings.branding.logo.invalidType',
          'Logo must be an image (PNG, JPG, SVG, WebP)'
        )
      );
      return;
    }
    setError(null);
    uploadLogo.mutate(file);
  }

  if (loadFailed) {
    return (
      <Card>
        <ErrorState
          title={t('settings.branding.loadError.title', "Couldn't load your branding")}
          description={
            friendlyMessage(loadError) ||
            t(
              'settings.branding.loadError.description',
              'Check your connection and try again. Nothing has been changed.'
            )
          }
          action={
            <Button variant="outline" onClick={() => void reloadBranding()}>
              {t('settings.branding.loadError.retry', 'Try again')}
            </Button>
          }
        />
      </Card>
    );
  }

  if (notLoaded) {
    return (
      <Card>
        <Loading label={t('settings.branding.loading', 'Loading branding…')} />
      </Card>
    );
  }

  return (
    <>
      {/* THIS PAGE KEEPS THE FULL WIDTH RATHER THAN OPTING DOWN TO `max-w-form`
          (see `pageWidth.test.tsx`'s header note), so — the same as
          `EmployeeFormPage`, `ProfilePage`, `LettersPage` and
          `WorksiteFormPage` — the element that lays the body into columns
          carries `data-page-columns`. Without the marker this page would be
          indistinguishable, by that test file's own check, from a page that
          silently stretched one reading column across the full 1600px: the
          asymmetry with `CompanyInfoPage` (which DOES opt down to
          `max-w-form`, having nothing to preview) is deliberate and load-
          bearing, and the marker is what states it rather than leaving it
          implicit in a grid class nobody is asserting on. */}
      <div data-page-columns="" className="grid grid-cols-1 gap-section lg:grid-cols-3">
        <div className="space-y-section lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.branding.logo.title', 'Logo')}</CardTitle>
              <CardDescription>
                {t(
                  'settings.branding.logo.recommendation',
                  'Recommended: 256×64 PNG with transparent background, under 2 MB.'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-component">
                {/* The drop target, nested inside the Logo card: `rounded-card`
                    (12px, inner card) is correct. The dashed edge is what says
                    "nothing here yet" without needing a colour. */}
                <div className="flex h-20 w-48 items-center justify-center rounded-card border border-dashed border-border bg-muted p-2">
                  {hasLogo ? (
                    <img
                      src={logoUrl(storeVersion || undefined)}
                      alt={t('settings.branding.logo.alt', 'Logo')}
                      className="h-full w-auto object-contain"
                    />
                  ) : (
                    <span className="text-copy-sm text-muted-foreground">
                      {t('settings.branding.logo.empty', 'No logo yet')}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-element">
                  {/* Operated by the button beside it, but it is still a real
                      control in the tree — without a name it announces as an
                      unlabelled "button". */}
                  <input
                    ref={fileInput}
                    id="logo-file"
                    type="file"
                    accept="image/*"
                    aria-label={t('settings.branding.logo.fileInputLabel', 'Logo image file')}
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <Button
                    type="button"
                    onClick={() => fileInput.current?.click()}
                    disabled={uploadLogo.isPending}
                  >
                    {uploadLogo.isPending
                      ? t('settings.branding.logo.uploading', 'Uploading…')
                      : hasLogo
                        ? t('settings.branding.logo.replace', 'Replace logo')
                        : t('settings.branding.logo.upload', 'Upload logo')}
                  </Button>
                  {hasLogo && (
                    <p className="text-copy-sm text-muted-foreground">
                      {t('settings.branding.logo.visibility', 'Visible on the login page and the sidebar.')}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('settings.branding.color.title', 'Color')}</CardTitle>
              {/* ONE COLOUR, and the copy says so.
                *
                * `--brand-primary` is a hex rather than the RGB channels every
                * other colour token uses, and that is deliberate: a tenant
                * supplies ONE colour, not a light/dark pair, so there is nothing
                * to compose an alpha against and nothing to invert. Re-seeding
                * the whole ramp from it — tint, hover, deep, and the dark
                * counterparts — is a follow-up that has not landed.
                *
                * So the honest description is what the value actually reaches
                * today, and the preview below is the check on it: `readableOn`
                * measures both candidate inks against the chosen fill and picks
                * the one that reads, rather than drawing a theme ink that flips
                * out from under the tenant's colour.
                *
                * THE COPY SAID "BUTTONS, LINKS AND ACCENTS ACROSS THE APP" AND
                * THAT WAS NEVER TRUE. Buttons and links are `--primary`, which
                * this value has never reached — the M3 ramp answers in two
                * themes and one hex cannot. For a day it did reach the nav
                * rail, alone, which is how the console came to show a red menu
                * beside a violet button and why the owner asked why the nav did
                * not follow the brand. It no longer does (stores/brandingStore.ts),
                * so the sentence now names the logo and the sign-in screen —
                * the two places the colour actually lands — and says plainly
                * what it does not do. §3: a setting that overstates its reach
                * is a screen stating something the product does not honour. */}
              <CardDescription>
                {t(
                  'settings.branding.color.description',
                  "Your mark's color — the logo tile and the sign-in screen. It does not repaint the app's buttons, links or navigation: those follow the product's own accent, which answers in both light and dark themes and is not something one color can set."
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ColorField
                label={t('settings.branding.color.primaryLabel', 'Primary')}
                value={draft.primaryColor}
                invalid={!primaryColorValid}
                onChange={(v) => handleColorChange('primaryColor', v)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                {t('settings.branding.appName.sectionTitle', 'App name & email footer')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-component">
              <div className="space-y-element">
                <Label htmlFor="appName">{t('settings.branding.appName.label', 'App name')}</Label>
                <Input
                  id="appName"
                  value={draft.appName}
                  maxLength={60}
                  onChange={(e) =>
                    setDraft({ ...draft, appName: e.target.value || 'Attendance' })
                  }
                />
                <p className="text-copy-sm text-muted-foreground">
                  {t(
                    'settings.branding.appName.hint',
                    'Shown on the login page, browser tab, and emails.'
                  )}
                </p>
              </div>
              <div className="space-y-element">
                <Label htmlFor="emailFooter">
                  {t('settings.branding.emailFooter.label', 'Email footer (optional)')}
                </Label>
                <Textarea
                  id="emailFooter"
                  rows={3}
                  value={draft.emailFooter ?? ''}
                  onChange={(e) =>
                    setDraft({ ...draft, emailFooter: e.target.value || null })
                  }
                />
              </div>
            </CardContent>
          </Card>

          {error && (
            <p className="text-copy text-danger" role="alert">
              {error}
            </p>
          )}
          {!colorsValid && (
            <p className="text-copy text-danger" role="alert">
              {t(
                'settings.branding.colorError',
                'Colors must be a 6-digit hex value like #6C47FF.'
              )}
            </p>
          )}
          {/* THE SUBMIT CLUSTER, at the end of the column and trailing.
              §4b settles the shape of a form once: sections down a reading
              column, submit at the bottom, primary last along the inline axis,
              never lifted into a toolbar. `justify-end` and not `ml-auto` — the
              inline axis flips under `dir="rtl"` and a physical margin does not.

              The Save button is blocked only when the colour is unreadable, and
              the sentence directly above it is the obstacle named: a disabled
              control that does not say why is the worst state in the product. */}
          <div className="flex flex-wrap justify-end gap-element">
            {/* `notLoaded` is defence in depth — the early returns above
                already keep this button off screen until branding is read. */}
            <Button
              onClick={() => saveBranding.mutate()}
              disabled={saveBranding.isPending || !colorsValid || notLoaded}
              aria-busy={saveBranding.isPending}
            >
              {saveBranding.isPending
                ? t('settings.branding.saving', 'Saving…')
                : t('settings.branding.save', 'Save branding')}
            </Button>
          </div>
        </div>

        <div>
          {/*
           * `top-header`, not `top-4`, and this is a shell change surfacing on a
           * screen rather than a taste change.
           *
           * The top bar became genuinely sticky in the shell work — before that
           * `<main>` owned `overflow-auto` and the header was its `shrink-0`
           * sibling, so `position: sticky` had nothing to stick to and a page's
           * own offset was measured against the viewport. The content column is
           * now the single scrollport, which means this card's offset is measured
           * from the same line the 56px bar occupies: at `top-4` the preview
           * slides sixteen pixels under the bar and its heading disappears.
           *
           * §2 of docs/design-rules.md: any page-level sticky sits at `top: 56px`,
           * never `top: 0`.
           *
           * `stickyBelowToolbar` and not the bare `top-header` it was fixed to,
           * because this card is the SECOND sticky element on a `PageShell`
           * page. There is no toolbar band here today, so the class resolves to
           * exactly 56px and nothing changes — but the moment branding grows one
           * (a preview device switcher, a theme toggle), a card parked at 56px
           * lands underneath it, and the obvious correction — `top-[104px]` — is
           * §6's anti-pattern verbatim, since the band's height depends on
           * whether its bar wrapped. The class reads `--header-height` plus the
           * band's MEASURED height, so neither number is ever written here.
           *
           * Sticky TABLE headers are unaffected — they scroll inside their own
           * `overflow-auto` box, so their scrollport is the card, not the page.
           */}
          <Card className={stickyBelowToolbar}>
            <CardHeader>
              <CardTitle>{t('settings.branding.preview.title', 'Preview')}</CardTitle>
              <CardDescription>
                {t('settings.branding.preview.description', 'What employees will see.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Nested inside the preview card, so `rounded-card` (12px, the
                  inner-card role) is correct and stays. */}
              <div className="overflow-hidden rounded-card border border-border">
                <div
                  className="flex h-12 items-center px-3"
                  style={{ backgroundColor: draft.primaryColor }}
                >
                  {hasLogo ? (
                    <img
                      src={logoUrl(storeVersion || undefined)}
                      alt={draft.appName}
                      className="h-7 w-auto object-contain"
                    />
                  ) : (
                    <span className="font-semibold text-secondary-foreground">{draft.appName}</span>
                  )}
                </div>
                <div className="space-y-inline bg-surface p-4">
                  {/* A caption naming the specimen below it — a field label, and
                      §1 of docs/design-rules.md bans field labels in caps twice
                      over. `text-label` is the 12.5px/600 sentence-case step it
                      should always have been; the deprecated `.section-title`
                      added a fourth uppercase role and a baked-in `mb-3` that
                      this site was already undoing. `space-y-inline` on the parent
                      owns the gap, so the `mb-0` override goes with it. */}
                  <p className="text-label text-muted-foreground">
                    {t('settings.branding.preview.loginButtonLabel', 'Login button')}
                  </p>
                  {/* The ink comes from the FILL, not from the theme.
                    *
                    * This drew `text-primary-foreground` on the tenant's own
                    * colour — the third instance of the same defect fixed in
                    * BrandLogo and AppVersion. `--primary-foreground` flips
                    * with the theme while `draft.primaryColor` does not, so in
                    * dark it painted near-black violet on the brand fill: 2.73:1
                    * on the live tenant blue, 2.24:1 on Al-Wathba red.
                    *
                    * It mattered most HERE, because this is the screen where an
                    * admin picks the colour — a preview that lies about its own
                    * contrast is worse than no preview. `readableOn` measures
                    * both candidate inks against the fill and returns the
                    * better, so the preview now shows what will actually ship. */}
                  <button
                    type="button"
                    className="h-10 w-full rounded-control font-medium"
                    style={{
                      backgroundColor: draft.primaryColor,
                      color: readableOn(draft.primaryColor),
                    }}
                  >
                    {t('settings.branding.preview.signIn', 'Sign in')}
                  </button>
                  <p className="text-label text-muted-foreground">
                    {t('settings.branding.preview.linkLabel', 'Link')}
                  </p>
                  <a
                    href="#preview"
                    className="text-copy font-medium underline"
                    style={{ color: draft.primaryColor }}
                  >
                    {draft.appName}
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
